import { useEffect, useState } from "react";
import { CheckCircle2, CreditCard, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]";
const inputCls =
  "w-full rounded-xl border border-line bg-cream/50 px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-rose";

export default function PaymentSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/site").then((r) => {
      const rz = r.data.settings?.razorpay || {};
      setForm({
        enabled: !!rz.enabled,
        mode: rz.mode || "test",
        key_id: rz.key_id || "",
        key_secret: "",
        webhook_secret: "",
        has_key_secret: !!rz.has_key_secret,
        has_webhook_secret: !!rz.has_webhook_secret,
        currency: rz.currency || "INR",
      });
    });
  }, []);

  if (!form) {
    return <div className="mt-6 flex justify-center py-10"><Loader2 size={22} className="animate-spin text-rose" /></div>;
  }

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); setError(""); };

  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await api.put("/admin/settings", {
        razorpay: {
          enabled: form.enabled,
          mode: form.mode,
          key_id: form.key_id,
          key_secret: form.key_secret,
          webhook_secret: form.webhook_secret,
          currency: form.currency,
        },
      });
      setSaved(true);
      setForm((f) => ({
        ...f,
        key_secret: "",
        webhook_secret: "",
        has_key_secret: f.has_key_secret || false,
      }));
    } catch {
      setError("Could not save payment settings.");
    } finally {
      setSaving(false);
    }
  };

  const webhookUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/api/payments/webhook`;

  return (
    <div data-testid="payment-settings" className="mt-6 max-w-lg rounded-3xl border border-line bg-white p-7 md:p-9">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-rose/10">
          <CreditCard size={18} className="text-rose" />
        </span>
        <div>
          <h3 className="font-serif text-2xl font-medium">Razorpay Payments</h3>
          <p className="text-xs text-[#5C5C5C]">Secrets are stored server-side only and never shown here.</p>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
          <input data-testid="rzp-enabled-toggle" type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 accent-[#C48B81]" />
          Enable online payments (Buy Now button on products)
        </label>
        <div>
          <label className={labelCls}>Mode</label>
          <select data-testid="rzp-mode-select" value={form.mode} onChange={(e) => set("mode", e.target.value)} className={inputCls}>
            <option value="test">Test Mode</option>
            <option value="live">Live Mode</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Razorpay Key ID</label>
          <input data-testid="rzp-key-id-input" value={form.key_id} onChange={(e) => set("key_id", e.target.value)} placeholder="rzp_test_..." className={inputCls} autoComplete="off" />
        </div>
        <div>
          <label className={labelCls}>Razorpay Key Secret</label>
          <input data-testid="rzp-key-secret-input" type="password" value={form.key_secret} onChange={(e) => set("key_secret", e.target.value)} placeholder={form.has_key_secret ? "••••••••  (saved — enter only to change)" : "Enter Key Secret"} className={inputCls} autoComplete="new-password" />
        </div>
        <div>
          <label className={labelCls}>Webhook Secret</label>
          <input data-testid="rzp-webhook-secret-input" type="password" value={form.webhook_secret} onChange={(e) => set("webhook_secret", e.target.value)} placeholder={form.has_webhook_secret ? "••••••••  (saved — enter only to change)" : "Enter Webhook Secret"} className={inputCls} autoComplete="new-password" />
          <p className="mt-1.5 break-all text-[11px] text-[#5C5C5C]">
            In the Razorpay dashboard, set the webhook URL to: <span className="font-mono">{webhookUrl}</span> (events: payment.captured, payment.failed)
          </p>
        </div>
        <div>
          <label className={labelCls}>Currency</label>
          <input data-testid="rzp-currency-input" value={form.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} className={inputCls} maxLength={3} />
        </div>

        {error && <p data-testid="rzp-save-error" className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">{error}</p>}
        {saved && (
          <p data-testid="rzp-save-success" className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-xs font-medium text-emerald-700">
            <CheckCircle2 size={15} /> Payment settings saved.
          </p>
        )}
        <button data-testid="rzp-save-button" onClick={save} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white disabled:opacity-60">
          {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : "Save Payment Settings"}
        </button>
      </div>
    </div>
  );
}
